import { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { Loader2, Trophy } from 'lucide-react';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { RaceRankingCard } from './RaceRankingCard';
import { MY_RACE_RESULTS } from '@/graphql/queries';

const PAGE_SIZE = 10;

export function ByRaceRankingTab() {
    const { t } = useTranslation('bets');
    const { t: tc } = useTranslation();
    const [page, setPage] = useState(1);

    const { data, loading, error, fetchMore } = useQuery(MY_RACE_RESULTS, {
        variables: { pagination: { page: 1, limit: PAGE_SIZE } },
    });

    const results = data?.myRaceResults?.items ?? [];
    const hasNextPage = data?.myRaceResults?.hasNextPage ?? false;

    const handleLoadMore = async () => {
        const nextPage = page + 1;
        await fetchMore({
            variables: { pagination: { page: nextPage, limit: PAGE_SIZE } },
            updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult?.myRaceResults) return prev;
                return {
                    myRaceResults: {
                        ...fetchMoreResult.myRaceResults,
                        items: [
                            ...(prev.myRaceResults?.items ?? []),
                            ...(fetchMoreResult.myRaceResults.items ?? []),
                        ],
                    },
                };
            },
        });
        setPage(nextPage);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20 text-slate-400">
                <Loader2 size={28} className="animate-spin text-emerald-600" />
            </div>
        );
    }

    if (error) {
        return <ErrorBanner message={tc('error.generic')} />;
    }

    if (results.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-dashed border-slate-200">
                <Trophy size={40} className="text-slate-200 mb-3" />
                <p className="text-sm font-medium text-slate-400 mb-1">
                    {t('ranking.byRace.empty')}
                </p>
                <p className="text-xs text-slate-400">
                    {t('ranking.byRace.emptyDesc')}
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {results.map((result) => (
                <RaceRankingCard
                    key={result.race?.id}
                    raceId={result.race?.id ?? ''}
                    raceName={result.race?.name ?? ''}
                    bettingDeadline={result.race?.bettingDeadline}
                    isOfficial={result.race?.isOfficial ?? false}
                    totalScore={result.totalScore ?? 0}
                    rank={result.rank ?? 0}
                    totalBettors={result.totalBettors ?? 0}
                />
            ))}

            {hasNextPage && (
                <div className="flex justify-center pt-2 pb-4">
                    <button
                        onClick={handleLoadMore}
                        className="px-6 py-3 rounded-2xl font-bold text-sm text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors active:scale-95"
                    >
                        {t('ranking.byRace.loadMore')}
                    </button>
                </div>
            )}
        </div>
    );
}
